import { SelectParam } from 'antd/lib/menu'
import qs from 'query-string'
import * as React from 'react'
import Menu from 'web-makeup/dist/Menu'
import Component from '@reactions/component'
import { getCurrentUser, getIntercomHash } from 'web-fns/dist/auth/auth'
import { colors } from 'web-makeup/dist/design-system/config/data/theme'
import styled from 'styled-components'

const { Item, SubMenu } = Menu

type QueryProps = Partial<{
  activated: boolean
  blocked: boolean
}>

const MenuItem = styled(Menu.Item)`
  &&&.ant-menu-item-selected {
    background-color: ${colors.action1};
  }
`

const MenuSubItem = styled(Item)`
  &&&.ant-menu-item-selected {
    background-color: ${colors.action1};
  }
`

type Path = '/producers' | '/creators'
const prefix = (path: Path) => (query?: QueryProps): string =>
  query === undefined ? path : `${path}?${qs.stringify(query)}`

const producerPrefix = prefix('/producers')

const getPath = (subpath: string) =>
  `${window.env && window.env.SIDE_MENU_BASE_URL}/${
    subpath.indexOf('/') === 0 ? subpath.substring(1) : subpath
  }`

enum MenuItemTitles {
  Brands = 'Brands',
  Creators = 'Creators',
  Producers = 'Producers',
  Opportunities = 'Opportunities',
  Projects = 'Projects',
  Requests = 'Requests',
  Support = 'Support',
  Finance = 'Finance',
  Settings = 'Settings',
  Old_reports = 'Old reports',
  Test = 'Test',
}

enum MenuItemKeys {
  brands = 'brands',
  creators = 'creators',
  producers = 'producers',
  opportunities = 'opportunities',
  projects = 'projects',
  requests = 'requests',
  support = 'support',
  finance = 'finance',
  settings = 'settings',
  reports = 'reports',
  test = 'test',
}

interface MenuProps {
  title: MenuItemTitles
  key: MenuItemKeys
  external?: boolean // added temporary until we have all necessary pages in Manager Gen3
  path?: string // added temporary until we have all necessary pages in Manager Gen3
  children?: Array<{
    id?: string
    title: string
    path: string
    key: string
    current?: object
    external?: boolean // added temporary until we have all necessary pages in Manager Gen3
  }>
}

const menu: MenuProps[] = [
  {
    title: MenuItemTitles.Brands,
    key: MenuItemKeys.brands,
    children: [
      {
        title: 'Activations',
        key: '0-0',
        path: getPath('/dashboards/brand_activation'),
        external: true,
      },
      {
        title: 'Engagement',
        key: '0-1',
        path: getPath('/dashboards/retention'),
        external: true,
      },
      {
        title: 'Top Brands',
        key: '0-2',
        path: getPath('/dashboards/top_brands'),
        external: true,
      },
    ],
  },
  {
    title: MenuItemTitles.Creators,
    key: MenuItemKeys.creators,
    children: [
      {
        title: 'Activations',
        key: '1-0',
        path: getPath('/dashboards/creator_activation'),
        external: true,
      },
      {
        title: 'Community',
        key: '1-1',
        path: getPath('/freelancers'),
        external: true,
      },
      {
        title: 'Coverage',
        key: '1-2',
        path: getPath('/freelancers/heatmap'),
        external: true,
      },
    ],
  },
  {
    title: MenuItemTitles.Producers,
    key: MenuItemKeys.producers,
    children: [
      {
        title: 'Community',
        key: '2-0',
        path: producerPrefix(),
      },
      {
        title: 'Activations',
        key: '2-1',
        current: { activated: false },
        path: producerPrefix({ activated: false }),
      },
      {
        title: 'Blocked',
        key: '2-2',
        current: { blocked: true },
        path: producerPrefix({ blocked: true }),
      },
    ],
  },
  {
    title: MenuItemTitles.Opportunities,
    key: MenuItemKeys.opportunities,
    children: [
      {
        title: 'Plans',
        key: '3-0',
        path: getPath('/dashboards/purchases'),
        external: true,
      },
      {
        title: 'Forecast',
        key: '3-1',
        path: getPath('/sales?human_id=all'),
        external: true,
      },
    ],
  },
  {
    title: MenuItemTitles.Projects,
    key: MenuItemKeys.projects,
    children: [
      {
        title: 'New Projects',
        key: '4-0',
        path: getPath('/video_projects'),
        external: true,
      },
      {
        title: 'All Projects',
        key: '4-1',
        path: getPath('/dashboards/success_management'),
        external: true,
      },
      {
        title: 'Project Overview',
        key: '4-2',
        path: getPath('/dashboards/video_projects_overview?type=next_job'),
        external: true,
      },
      {
        title: 'Reviews',
        key: '4-3',
        path: getPath(
          '/dashboards/video_projects_overview?review=awaiting&type=completed_projects'
        ),
        external: true,
      },
      {
        title: 'Forecast',
        key: '4-4',
        path: getPath('/dashboards/creation_forecast'),
        external: true,
      },
    ],
  },
  {
    title: MenuItemTitles.Requests,
    key: MenuItemKeys.requests,
    path: '/requests',
  },
  {
    title: MenuItemTitles.Finance,
    key: MenuItemKeys.finance,
    children: [
      {
        title: 'Payments In',
        key: '6-0',
        path: getPath('/dashboards/receivable_invoices'),
        external: true,
      },
      {
        title: 'Payments Out',
        key: '6-1',
        path: getPath('/dashboards/pay_freelancers'),
        external: true,
      },
      {
        title: 'Revenue Adjustments',
        key: '6-2',
        path: getPath('/finance_dashboards/revenue_report'),
        external: true,
      },
    ],
  },
  {
    title: MenuItemTitles.Settings,
    key: MenuItemKeys.settings,
    external: true,
    path: getPath('/settings/'),
  },
  {
    title: MenuItemTitles.Old_reports,
    key: MenuItemKeys.reports,
    external: true,
    path: getPath('/dashboards/old_90_reports'),
  },
  {
    title: MenuItemTitles.Support,
    key: MenuItemKeys.support,
    children: [
      {
        title: 'Support Content',
        key: '8-0',
        path: getPath('/support/manager'),
        external: true,
      },
    ],
  },
]

const getSubMenu = key => menu.find(item => item.key === key)

export const handleMenuSelect = (params: SelectParam, history) => {
  const indexPath = params.key.split('-')
  let parent: MenuProps | undefined = menu.find(m => m.key === params.key)

  // This will have to be like this temporary till we have our own page in Gen3
  // It was requested by George to add all pages in the manager
  if (parent && parent.external && parent.path) {
    window.open(parent.path)
  } else {
    if (indexPath.length === 1) {
      history.push(`/${indexPath[0]}`)
    }
    parent = menu[parseInt(indexPath[0], 10)]
    const child = parent && parent.children && parent.children[parseInt(indexPath[1], 10)]
    if (child) {
      !child.external ? history.push(child.path) : window.open(child.path)
    }
  }
}

const getDefaultSelectedKeys = (links, match) =>
  !links.children
    ? links.key
    : links.children.find(link => link.path.replace('/', '') === match.url.split('/')[1]).key

const SideMenu = props => (
  <Component
    didMount={() => {
      const currentUser = getCurrentUser()
      const intercomHash = getIntercomHash()
      if (currentUser && currentUser.email && intercomHash) {
        window.intercomSettings = {
          app_id: window.env.INTERCOM_APP_ID,
          name: currentUser.name, // Full name
          user_id: currentUser.id,
          email: currentUser.email, // Email address
          user_hash: intercomHash,
        }
      }
    }}
  >
    {() => (
      <Menu
        data-testid="side-menu"
        defaultSelectedKeys={
          props.match && [
            getDefaultSelectedKeys(getSubMenu(props.match.url.split('/')[1]), props.match),
          ]
        }
        defaultOpenKeys={props.match && [props.match.url.split('/')[1]]}
        mode="inline"
        theme="dark"
        onClick={params => handleMenuSelect(params as SelectParam, props.history)}
        onSelect={params => handleMenuSelect(params, props.history)}
      >
        {menu.map((item, index) => {
          if (item.children) {
            return (
              <SubMenu
                data-testid={`submenu-${item.key}`}
                key={item.key}
                title={<span>{item.title}</span>}
              >
                {item.children.map(child => (
                  <MenuSubItem id={child.id} data-testid={`item-${child.key}`} key={child.key}>
                    {child.title}
                  </MenuSubItem>
                ))}
              </SubMenu>
            )
          }

          return (
            <MenuItem key={item.key}>
              <span>{item.title}</span>
            </MenuItem>
          )
        })}
      </Menu>
    )}
  </Component>
)

export default SideMenu



// WEBPACK FOOTER //
// ./src/components/SideMenu/SideMenu.tsx